import React from 'react';
import { RefineThemes } from '@refinedev/antd';
import { ConfigProvider, theme } from 'antd';
import {
  type PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from 'react';
import { colors } from '@/constants';
import useLangStore from '@/core/lang';

type ColorModeContextType = {
  mode: string;
  setMode: (mode: string) => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { locale } = useLangStore();
  const colorModeFromLocalStorage = localStorage.getItem('colorMode');
  const isSystemPreferenceDark = window?.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;

  const systemPreference = isSystemPreferenceDark ? 'dark' : 'light';
  const [mode, setMode] = useState(
    colorModeFromLocalStorage || systemPreference
  );

  useEffect(() => {
    window.localStorage.setItem('colorMode', mode);
    document.documentElement.classList.toggle('dark', mode === 'dark');
  }, [mode]);

  const setColorMode = () => {
    if (mode === 'light') {
      setMode('dark');
    } else {
      setMode('light');
    }
  };

  const { darkAlgorithm, defaultAlgorithm } = theme;
  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <ConfigProvider
        theme={{
          ...RefineThemes.Blue,
          algorithm: mode === 'light' ? defaultAlgorithm : darkAlgorithm,
          token: {
            colorPrimary: colors.secondary.DEFAULT,
          },
        }}
        direction={locale === 'ar' ? 'rtl' : locale === 'en' ? 'ltr' : 'rtl'}
      >
        {children}
      </ConfigProvider>
    </ColorModeContext.Provider>
  );
};
